import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useShipmentWebSocket from 'common/hooks/useShipmentWebSocket';
import PropTypes from 'prop-types';
import { useState } from 'react';
import stc from 'string-to-color';

const ShipmentRoomUsers = ({ shipmentId }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { currentUsers } = useShipmentWebSocket(shipmentId);

  if (!currentUsers || currentUsers.length === 0) return null;

  const capitalizeName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const maxVisibleUsers = 5;
  const visibleUsers = currentUsers.slice(0, maxVisibleUsers);
  const remainingCount = Math.max(0, currentUsers.length - maxVisibleUsers);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        variant='caption'
        sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: 500 }}
      >
        {currentUsers.length} viewing
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {visibleUsers.map((user) => {
          const firstName = capitalizeName(user.firstName);
          const lastName = capitalizeName(user.lastName);
          const fullName = `${firstName} ${lastName}`;
          const bgcolor = stc(fullName);

          return (
            <Tooltip key={user.userId} title={`${firstName} ${lastName}`} arrow placement='bottom'>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: '0.7rem',
                  backgroundColor: bgcolor,
                  color: theme.palette.getContrastText(bgcolor),
                  cursor: 'pointer',
                  border: '2px solid rgba(255,255,255,0.6)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  '&:hover': { transform: 'scale(1.1)', transition: 'transform 0.2s ease-in-out' },
                }}
              >
                {firstName?.[0]}
                {lastName?.[0]}
              </Avatar>
            </Tooltip>
          );
        })}

        {remainingCount > 0 && (
          <>
            <Avatar
              onClick={handleClick}
              sx={{
                width: 30,
                height: 30,
                fontSize: '0.7rem',
                backgroundColor: '#6c757d',
                color: 'white',
                cursor: 'pointer',
                border: '2px solid rgba(255,255,255,0.6)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s ease-in-out',
                  backgroundColor: '#5a6268',
                },
              }}
            >
              +{remainingCount}
            </Avatar>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { maxHeight: 300, width: 250, mt: 1 } }}
            >
              <Paper sx={{ p: 1 }}>
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 'bold', color: '#495057', mb: 1, px: 1 }}
                >
                  All Users Viewing ({currentUsers.length})
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {currentUsers.map((user) => {
                    const firstName = capitalizeName(user.firstName);
                    const lastName = capitalizeName(user.lastName);
                    const fullName = `${firstName} ${lastName}`;
                    const bgcolor = stc(fullName);

                    return (
                      <ListItem key={user.userId} sx={{ py: 0.5, px: 1 }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: '0.75rem',
                              backgroundColor: bgcolor,
                              color: 'white',
                            }}
                          >
                            {firstName?.[0]}
                            {lastName?.[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${firstName} ${lastName}`}
                          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};

ShipmentRoomUsers.propTypes = {
  shipmentId: PropTypes.string.isRequired,
};

export default ShipmentRoomUsers;
